import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardCardIcon from './icons/DashboardCardIcon';
import {
  markTrainingLoginAlertShown,
  wasTrainingLoginAlertShown,
} from '../utils/trainingLoginAlert';

const TRAINING_BLUE = '#2196F3';

function pickAuthToken(u) {
  if (!u || typeof u !== 'object') return '';
  return String(
    u.token || u.session_token || u.sessionToken || u.access_token || u.accessToken || '',
  ).trim();
}

function formatPeriod(it) {
  const start = String(it?.due_date_start || '').trim();
  const end = String(it?.due_date_end || '').trim();
  if (start && end) return `${start} ~ ${end}`;
  if (end) return `~ ${end}`;
  if (start) return `${start} ~`;
  return '해당 기간';
}

/** 로그인할 때마다(새 세션 토큰) 미이수 사내교육 안내 모달 */
export default function TrainingPendingLoginAlert() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const email = String(user?.email || '').trim().toLowerCase();
  const token = pickAuthToken(user);

  useEffect(() => {
    if (!isAuthenticated || !email || !token) return undefined;
    if (wasTrainingLoginAlertShown(email, token)) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const headers = {
          Authorization: `Bearer ${token}`,
          'X-MRT-Token': token,
        };
        const res = await fetch('/api/erp_internal_training/pending', {
          method: 'GET',
          headers,
          credentials: 'include',
        });
        if (!res.ok || cancelled) return;
        const data = await res.json().catch(() => null);
        const list = Array.isArray(data?.items) ? data.items : [];
        if (cancelled || list.length === 0) {
          markTrainingLoginAlertShown(email, token);
          return;
        }
        setItems(list);
        setOpen(true);
        markTrainingLoginAlertShown(email, token);
      } catch {
        /* ignore */
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, email, token]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !items.length) return null;

  const close = () => setOpen(false);
  const goTraining = () => {
    setOpen(false);
    navigate('/erp?menu=internal_training');
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(15,23,42,0.62)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-training-title"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.28)',
        }}
      >
        <div style={{ padding: '22px 22px 14px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ flexShrink: 0, lineHeight: 0 }}>
              <DashboardCardIcon cardId="internal-training" />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: TRAINING_BLUE, marginBottom: 6 }}>
                로그인 안내 · 사내교육 미이수
              </div>
              <h3
                id="login-training-title"
                style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1.35 }}
              >
                수료하지 않은 교육이 있습니다
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                해당 기간 내 이수를 완료해 주세요. 미이수 시 감봉 대상이 될 수 있습니다.
              </p>
            </div>
          </div>
        </div>

        <div style={{ padding: '14px 22px', overflowY: 'auto', flex: 1 }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it) => (
              <li
                key={it.seq}
                style={{
                  border: `1px solid ${it.overdue ? '#fecaca' : 'rgba(33,150,243,0.22)'}`,
                  borderRadius: 14,
                  padding: '14px 16px',
                  background: it.overdue ? '#fef2f2' : '#f8fbff',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', lineHeight: 1.55, wordBreak: 'keep-all' }}>
                  {it.title || '교육'}
                  {' '}
                  과정의 교육을 해당기간동안 수료하지 않았습니다~
                  {it.overdue ? (
                    <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>기한 초과</span>
                  ) : null}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
                  기간 {formatPeriod(it)}
                  {' · '}
                  진행 {Math.round(it.progress_percent || 0)}%
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div
          style={{
            padding: '16px 22px 20px',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={close}
            style={{
              flex: '1 1 120px',
              padding: '14px 0',
              borderRadius: 14,
              border: '1px solid #cbd5e1',
              background: '#fff',
              color: '#334155',
              fontWeight: 800,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            확인
          </button>
          <button
            type="button"
            onClick={goTraining}
            style={{
              flex: '1 1 160px',
              padding: '14px 0',
              borderRadius: 14,
              border: 'none',
              background: `linear-gradient(135deg, #1976D2, ${TRAINING_BLUE})`,
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(33,150,243,0.32)',
            }}
          >
            사내교육 이수하기
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
